import React, {
  useState,
  useContext,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import {
  Bars3Icon,
  BellIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useNavigation } from '../context/NavigationContext';
import { NotificationContext } from '../context/NotificationContext';
import { AuthContext } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import userImage from '../assets/images/user.webp';
import api from '../services/ApiService';

const navigation = [
  { name: 'Dashboard', href: '/dashboard' },
  { name: 'Notifications', href: '/notifications' },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const Header = () => {
  const { current, setCurrent } = useNavigation();
  const { notifications, notificationCount } = useContext(NotificationContext);
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationMenuOpen, setNotificationMenuOpen] = useState(false);

  const notificationRef = useRef(null);
  const userMenuRef = useRef(null);

  const toggleUserMenu = useCallback(() => setUserMenuOpen((prev) => !prev), []);
  const toggleNotificationMenu = useCallback(() => setNotificationMenuOpen((prev) => !prev), []);

  // ✅ Dismiss dropdown on outside click or Esc
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationMenuOpen &&
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setNotificationMenuOpen(false);
      }

      if (
        userMenuOpen &&
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target)
      ) {
        setUserMenuOpen(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setUserMenuOpen(false);
        setNotificationMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [notificationMenuOpen, userMenuOpen]);

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const pageTitle = useMemo(() => {
    const currentPage = navigation.find((item) => item.href === current);
    return currentPage ? currentPage.name : 'Dashboard';
  }, [current]);

  const categorizedNotifications = useMemo(() => {
    const cat = { info: [], success: [], warning: [], error: [] };
    notifications.forEach((n) => {
      if (cat[n.type]) cat[n.type].push(n);
    });
    return cat;
  }, [notifications]);

  return (
    <nav className="bg-cyan-950">
      <div className="mx-auto px-2 sm:px-6 lg:px-8">
        <div className="relative flex h-16 items-center justify-between">
          <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
            <button
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:bg-gray-200 hover:text-gray-800"
              onClick={() => setMenuOpen((prev) => !prev)}
            >
              <span className="sr-only">Open main menu</span>
              {menuOpen ? (
                <XMarkIcon className="block h-6 w-6" />
              ) : (
                <Bars3Icon className="block h-6 w-6" />
              )}
            </button>
          </div>

          <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
            <div className="flex-shrink-0 text-white text-xl font-semibold">Pilari Web App</div>
            <div className="hidden sm:ml-6 sm:block">
              <div className="flex space-x-4">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setCurrent(item.href)}
                    className={classNames(
                      current === item.href
                        ? 'bg-gray-100 text-gray-900'
                        : 'text-gray-500 hover:bg-gray-200 hover:text-gray-800',
                      'rounded-md px-3 py-2 text-sm font-medium'
                    )}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
            <div className="relative">
              <button
                type="button"
                className="relative rounded-full bg-white p-1 text-gray-400 hover:text-gray-800"
                onClick={toggleNotificationMenu}
              >
                <BellIcon className="h-6 w-6" />
                {notificationCount > 0 && (
                  <span className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 px-2 py-1 text-xs font-bold text-red-100 bg-red-600 rounded-full">
                    {notificationCount}
                  </span>
                )}
              </button>

              {notificationMenuOpen && (
                <div
                  ref={notificationRef}
                  className="absolute right-0 mt-2 w-64 bg-white shadow-lg rounded-md z-50 py-1 ring-1 ring-black ring-opacity-5"
                >
                  <div className="px-4 py-2">
                    <h3 className="text-sm font-semibold text-gray-700">Notifications</h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {['info', 'success', 'warning', 'error'].map((type) => (
                      <div key={type} className="px-4 py-2">
                        <h4 className="text-xs font-semibold text-gray-600 capitalize">{type}</h4>
                        {categorizedNotifications[type].length === 0 ? (
                          <p className="text-xs text-gray-500">No {type} notifications</p>
                        ) : (
                          categorizedNotifications[type].slice(0, 3).map((n) => (
                            <p key={n._id} className="text-xs text-gray-700">
                              {n.message}
                            </p>
                          ))
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-2">
                    <button
                      onClick={() => {
                        setCurrent('/notifications');
                        navigate('/notifications');
                        setNotificationMenuOpen(false);
                      }}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      See All
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="relative ml-3">
              <button
                className="relative flex rounded-full bg-white text-sm"
                onClick={toggleUserMenu}
              >
                <img className="h-8 w-8 rounded-full" src={userImage} alt="User" />
              </button>
              {userMenuOpen && (
                <div
                  ref={userMenuRef}
                  className="absolute right-0 z-10 mt-2 w-48 bg-white shadow-lg rounded-md py-1 ring-1 ring-black ring-opacity-5"
                >
                  <Link
                    to="/profile"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    Your Profile
                  </Link>
                  <Link
                    to="/settings"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    Settings
                  </Link>
                  <button
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <header className="bg-gray-300 text-black p-3">
        <h1 className="text-lg font-bold">{pageTitle}</h1>
      </header>

      {menuOpen && (
        <div className="sm:hidden">
          <div className="space-y-1 px-2 pb-3 pt-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => {
                  setCurrent(item.href);
                  setMenuOpen(false);
                }}
                className={classNames(
                  current === item.href
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-500 hover:bg-gray-200 hover:text-gray-800',
                  'block rounded-md px-3 py-2 text-base font-medium'
                )}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default React.memo(Header);
